import { buildAmplitudeExperimentTrackingTrait } from '@redbubble/next-amplitude';
import logger from '@redbubble/next-logger';
import { sessionStorage } from '@redbubble/persistent-storage';
import { useEffect } from 'react';
import { useRudderstackClient } from './rudderstack-provider';
export var IDENTIFY_THROTTLE_KEY = 'rs_last_identify';
var IDENTIFY_THROTTLE_MS = 15 * 60 * 1000; // 15 minutes
var exposureListeners = new Set();
/**
 * Notify subscribers that a new experiment exposure has fired. Called by
 * useAmplitudeExperiment so a late exposure — e.g. a conditionally rendered
 * component (cart tray) or a client-side route transition that mounts an
 * experiment in a later commit — can re-trigger the deferred identify, rather
 * than being missed until the next request.
 */
export function notifyExposure() {
    exposureListeners.forEach(function (listener) { return listener(); });
}
function subscribeToExposure(listener) {
    exposureListeners.add(listener);
    return function () {
        exposureListeners.delete(listener);
    };
}
function shouldIdentify(identifyCallProps) {
    try {
        var raw = sessionStorage.getItem(IDENTIFY_THROTTLE_KEY);
        if (!raw)
            return true;
        var prev = JSON.parse(raw);
        if (prev.federatedId !== identifyCallProps.federatedId)
            return true;
        if (Date.now() - prev.ts > IDENTIFY_THROTTLE_MS)
            return true;
        return false;
    }
    catch (err) {
        var message = 'Session storage read failed for identify throttle check';
        logger.warn({ err: err, message: message, context: 'RudderstackDefaultTracking' }, message);
        return true;
    }
}
function recordIdentify(identifyCallProps) {
    try {
        sessionStorage.setItem(IDENTIFY_THROTTLE_KEY, JSON.stringify({
            ts: Date.now(),
            federatedId: identifyCallProps.federatedId,
        }));
    }
    catch (err) {
        var message = 'Failed to write identify throttle to session storage';
        logger.warn({ err: err, message: message, context: 'RudderstackDefaultTracking' }, message);
    }
}
export var RudderstackIdentifyTracking = function (_a) {
    var identifyCallProps = _a.identifyCallProps;
    var RudderstackClient = useRudderstackClient().RudderstackClient;
    useEffect(function () {
        if (!RudderstackClient)
            return;
        // Coalesce a burst of exposures within one flush into a single identify;
        // ignore an already-queued callback after the effect is torn down (cleanup
        // cannot cancel a microtask, unlike clearTimeout).
        var scheduled = false;
        var cancelled = false;
        // Defer the identify call to a microtask so it runs after the current
        // effect flush. By then every useAmplitudeExperiment hook committed in this
        // flush has fired its exposure event and recorded it to localStorage.
        // Building the experiments trait at fire time (rather than synchronously)
        // ensures the `exposed` values are accurate — see
        // buildAmplitudeExperimentTrackingTrait.
        //
        // Rescheduling on every exposure notification does double duty: multiple
        // exposures in a single flush coalesce into one identify, and a late
        // exposure (a component that mounts in a later commit) re-fires identify
        // with its accurate state instead of being missed until the next request.
        var scheduleIdentify = function () {
            if (scheduled)
                return;
            scheduled = true;
            queueMicrotask(function () {
                var _a, _b, _c, _d;
                scheduled = false;
                if (cancelled)
                    return;
                if (!shouldIdentify(identifyCallProps))
                    return;
                RudderstackClient.identify(identifyCallProps.federatedId, {
                    federated_id: identifyCallProps.federatedId,
                    is_logged_in: identifyCallProps.isLoggedIn,
                    cf_bot_score: (_a = identifyCallProps.cloudflare) === null || _a === void 0 ? void 0 : _a.botScore,
                    cf_device_type: (_b = identifyCallProps.cloudflare) === null || _b === void 0 ? void 0 : _b.deviceType,
                    cf_ip_country: (_c = identifyCallProps.cloudflare) === null || _c === void 0 ? void 0 : _c.ipCountry,
                    cf_ja4: (_d = identifyCallProps.cloudflare) === null || _d === void 0 ? void 0 : _d.ja4,
                    experiments: buildAmplitudeExperimentTrackingTrait(identifyCallProps.amplitudeExperiments),
                });
                recordIdentify(identifyCallProps);
            });
        };
        scheduleIdentify();
        var unsubscribe = subscribeToExposure(scheduleIdentify);
        return function () {
            cancelled = true;
            unsubscribe();
        };
    }, [RudderstackClient, identifyCallProps]);
    return null;
};
